<template>
  <div class="linkInfoPage divContainer genericResourceModule">
    <div v-if="loading" v-loading="loading" class="fullContainer" />

    <div v-else-if="loadError" class="linkInfoPage__error">
      <p class="linkInfoPage__errorTitle">资源加载失败</p>
      <p class="linkInfoPage__errorDesc link-text-muted">{{ loadError }}</p>
      <el-button size="small" type="primary" @click="goToList">返回列表</el-button>
    </div>

    <div v-else class="linkInfoPage__layout resourceFormPage__layout">
      <div class="resourceFormPage__headerDock">
        <header class="resourceFormPage__actionBar">
          <div class="resourceFormPage__lead">
            <h1 class="resourceFormPage__pageTitle">查看资源</h1>
            <span v-if="info.assetType" class="resourceFormPage__typeReadonly link-font-mono">
              {{ info.assetType }}
            </span>
          </div>
          <div class="resourceFormPage__actions">
            <button
              type="button"
              class="resourceFormPage__btn resourceFormPage__btn--ghost"
              :disabled="!hasZipUrl"
              @click="downloadResourceZip"
            >
              下载资源
            </button>
            <button
              v-if="info.onOrOff === 0"
              type="button"
              class="resourceFormPage__btn resourceFormPage__btn--ghost"
              @click="toggleOnlineStatus"
            >
              上线
            </button>
            <button
              v-else-if="info.onOrOff === 1"
              type="button"
              class="resourceFormPage__btn resourceFormPage__btn--ghost"
              @click="toggleOnlineStatus"
            >
              下线
            </button>
            <button type="button" class="resourceFormPage__btn resourceFormPage__btn--ghost" @click="deleteResource">
              删除
            </button>
            <button type="button" class="resourceFormPage__btn resourceFormPage__btn--primary" @click="goToEdit">
              编辑
            </button>
          </div>
        </header>
      </div>

      <main
        class="linkInfoPage__main resourceFormPage__main main"
        :class="{ 'linkInfoPage__main--jsonOpen': jsonSheetOpen }"
      >
        <generic-resource-json-top-sheet
          :visible.sync="jsonSheetOpen"
          :payload="jsonSheetPayload"
        >
        <!-- 基本信息：左预览图锚点 + 右字段网格，消除中间死区 -->
        <el-card class="card linkInfoSection">
          <div slot="header">
            <strong class="linkInfoSection__title">基本信息</strong>
          </div>
          <div class="linkInfoBasic">
            <div class="linkInfoBasic__preview">
              <div
                v-if="showCollectionCoverSwitch"
                class="linkInfoPreview__genderSwitch"
                role="tablist"
                aria-label="合集封面性别"
              >
                <button
                  type="button"
                  role="tab"
                  class="linkInfoPreview__genderBtn"
                  :class="{ 'linkInfoPreview__genderBtn--active': coverGender === 'female' }"
                  :aria-selected="coverGender === 'female'"
                  :disabled="!collectionCovers.female"
                  @click="coverGender = 'female'"
                >
                  女封面
                </button>
                <button
                  type="button"
                  role="tab"
                  class="linkInfoPreview__genderBtn"
                  :class="{ 'linkInfoPreview__genderBtn--active': coverGender === 'male' }"
                  :aria-selected="coverGender === 'male'"
                  :disabled="!collectionCovers.male"
                  @click="coverGender = 'male'"
                >
                  男封面
                </button>
              </div>
              <el-image
                v-if="previewThumbUrl"
                class="linkInfoPreview__img"
                :src="previewThumbUrl"
                :preview-src-list="previewSrcList"
                fit="cover"
              >
                <div slot="error" class="linkInfoPreview__empty">
                  <i class="el-icon-picture-outline" aria-hidden="true" />
                  <span>加载失败</span>
                </div>
              </el-image>
              <div v-else class="linkInfoPreview__empty">
                <i class="el-icon-picture-outline" aria-hidden="true" />
                <span>暂无预览图</span>
              </div>
            </div>

            <div class="linkInfoBasic__body">
              <h1 class="linkInfoBasic__name">{{ displayName }}</h1>

              <div class="linkInfoBasic__keyRow">
                <span class="linkInfoBasic__keyLabel">Key</span>
                <template v-if="info.key">
                  <code class="linkInfoBasic__keyValue link-font-mono" :title="info.key">
                    {{ displayKey }}
                  </code>
                  <button type="button" class="linkInfoBasic__copyBtn" @click="copyKey">
                    复制
                  </button>
                </template>
                <span v-else class="linkInfoSection__empty">—</span>
              </div>

              <div class="linkInfoBasic__grid">
                <div class="linkInfoBasic__cell">
                  <div class="linkInfoBasic__label">类型</div>
                  <span
                    v-if="info.assetType"
                    class="linkInfoPill linkInfoPill--type"
                    :class="`linkInfoPill--type-${getTypeTagVariant(info.assetType)}`"
                  >
                    {{ info.assetType }}
                  </span>
                  <span v-else class="linkInfoSection__empty">—</span>
                </div>

                <div class="linkInfoBasic__cell">
                  <div class="linkInfoBasic__label">状态</div>
                  <span
                    class="statusPill"
                    :class="'statusPill--' + getStatusPillVariant(info.onOrOff)"
                  >
                    <span class="statusPill__dot" aria-hidden="true" />
                    {{ getStatusLabel(info.onOrOff) }}
                  </span>
                </div>

                <div class="linkInfoBasic__cell">
                  <div class="linkInfoBasic__label">分类</div>
                  <div
                    v-if="info.categoryList && info.categoryList.length"
                    class="linkInfoBasic__tagRow"
                  >
                    <router-link
                      v-for="item in info.categoryList"
                      :key="item.id"
                      :to="{ path: '/collection/classify/info/' + item.id }"
                      class="linkInfoBasic__categoryLink"
                    >
                      <span class="linkInfoPill linkInfoPill--category">{{ item.name }}</span>
                    </router-link>
                  </div>
                  <span v-else class="linkInfoSection__empty">未关联</span>
                </div>

                <div class="linkInfoBasic__cell">
                  <div class="linkInfoBasic__labelRow">
                    <span class="linkInfoBasic__label linkInfoBasic__label--inline">配置数据集</span>
                    <span class="linkInfoBasic__hint">
                      只统计下面「关联解锁」「过滤条件」里填过的数据集；列表按数据集筛选看的是分类是否在该数据集里，两边可能对不上。
                    </span>
                  </div>
                  <div v-if="datasetNames.length" class="linkInfoBasic__tagRow">
                    <span
                      v-for="name in datasetNames"
                      :key="name"
                      class="linkInfoPill linkInfoPill--dataset"
                    >
                      {{ name }}
                    </span>
                  </div>
                  <span v-else class="linkInfoSection__empty">未配置</span>
                </div>
              </div>
            </div>
          </div>
        </el-card>

        <generic-resource-workout-readonly :info="info" />

        <generic-resource-sticker-readonly :info="info" />

        <generic-resource-ai-assistant-readonly :info="info" />

        <generic-resource-unlock-readonly :info="info" />

        <!-- 关联 Tag：全量展示，自动换行占满卡片宽度 -->
        <el-card class="card linkInfoSection">
          <div slot="header" class="linkInfoSection__head">
            <strong class="linkInfoSection__title">关联 Tag</strong>
            <span v-if="tagCount" class="linkInfoSection__badge">{{ tagCount }}</span>
          </div>

          <div v-if="tagCount" class="linkInfoTagList">
            <span
              v-for="tag in info.tagVOList"
              :key="tag.key || tag.id"
              class="linkInfoPill linkInfoPill--tag"
            >
              {{ tag.name }}
            </span>
          </div>
          <span v-else class="linkInfoSection__empty">未关联 Tag</span>
        </el-card>

        <generic-resource-filter-readonly :info="info" />

        <!-- 其他：只读备注，不用 disabled 表单 -->
        <el-card class="card linkInfoSection">
          <div slot="header">
            <strong class="linkInfoSection__title">其他</strong>
          </div>
          <div class="linkInfoRemark">
            <div class="linkInfoRemark__label">备注</div>
            <p v-if="info.comment" class="linkInfoRemark__text">{{ info.comment }}</p>
            <span v-else class="linkInfoSection__empty">无备注</span>
          </div>
        </el-card>
        </generic-resource-json-top-sheet>
      </main>
    </div>
  </div>
</template>

<script>
import {
  getGenericResourceById,
  deleteGenericResource,
  onOrOff,
} from '../../../api/genericResource'
import GenericResourceFilterReadonly from './GenericResourceFilterReadonly.vue'
import GenericResourceJsonTopSheet from './GenericResourceJsonTopSheet.vue'
import GenericResourceUnlockReadonly from './GenericResourceUnlockReadonly.vue'
import GenericResourceWorkoutReadonly from './GenericResourceWorkoutReadonly.vue'
import GenericResourceStickerReadonly from './GenericResourceStickerReadonly.vue'
import GenericResourceAiAssistantReadonly from './GenericResourceAiAssistantReadonly.vue'
import { normalizeGenericResourceDetail } from './genericResourceAdapter'
import { getStatusLabel, getStatusPillVariant, getTypeTagVariant, resourceTitle } from './batchTag/resourceDisplayHelper'
import {
  buildJsonConfigViewPayload,
  collectResourceDatasetNames,
  downloadResourceZipFile,
  normalizeAppVersionListForView,
  parseExtaFromConfig,
  resolveCollectionCoverUrls,
  resolveResourceZipUrl,
} from './genericResourceInfoHelper'
import { resolveLayoutBackTo, buildLocationWithBackTo } from '../../../utils/layoutNavigation'

/**
 * 通用资源查看页 v5。
 * 基本信息含配置数据集 pill 汇总；关联解锁与过滤条件固定维度展示明细。
 */
export default {
  name: 'GenericResourceInfo',
  components: {
    GenericResourceFilterReadonly,
    GenericResourceJsonTopSheet,
    GenericResourceUnlockReadonly,
    GenericResourceWorkoutReadonly,
    GenericResourceStickerReadonly,
    GenericResourceAiAssistantReadonly,
  },
  data() {
    return {
      info: {
        jsonConfig: {},
        categoryList: [],
        tagVOList: [],
        wallpaperLockGroupVOList: [],
        wallpaperAppVersionList: [],
        countryList: {},
        comment: '',
      },
      loading: false,
      loadError: '',
      jsonSheetOpen: false,
      /** 合集详情：女/男封面切换；默认女（与列表 thumbUrl 一致） */
      coverGender: 'female',
      processingTimer: null,
    }
  },
  computed: {
    displayName() {
      return resourceTitle(this.info)
    },
    collectionCovers() {
      if (this.info.assetType !== 'workout_collection') {
        return { female: '', male: '' }
      }
      return resolveCollectionCoverUrls(parseExtaFromConfig(this.info.jsonConfig))
    },
    showCollectionCoverSwitch() {
      return Boolean(this.collectionCovers.female || this.collectionCovers.male)
    },
    previewThumbUrl() {
      if (this.showCollectionCoverSwitch) {
        const preferred = this.collectionCovers[this.coverGender]
        return (
          preferred ||
          this.collectionCovers.female ||
          this.collectionCovers.male ||
          this.info.thumbUrl ||
          ''
        )
      }
      return this.info.thumbUrl || ''
    },
    previewSrcList() {
      if (!this.previewThumbUrl) {
        return []
      }
      if (!this.showCollectionCoverSwitch) {
        return [this.previewThumbUrl]
      }
      return [this.collectionCovers.female, this.collectionCovers.male].filter(Boolean)
    },
    /** Key 中间省略，悬停 title 看全文。 */
    displayKey() {
      const key = this.info.key
      if (!key) {
        return '—'
      }
      if (key.length <= 36) {
        return key
      }
      return `${key.slice(0, 16)}…${key.slice(-12)}`
    },
    jsonSheetPayload() {
      return buildJsonConfigViewPayload(this.info)
    },
    datasetNames() {
      return collectResourceDatasetNames(
        this.info.wallpaperLockGroupVOList,
        this.info.wallpaperAppVersionList
      )
    },
    tagCount() {
      return (this.info.tagVOList || []).length
    },
    hasZipUrl() {
      return Boolean(resolveResourceZipUrl(this.info))
    },
  },
  created() {
    this.loadDetail()
  },
  beforeDestroy() {
    this.stopProcessingPoll()
  },
  methods: {
    getTypeTagVariant,
    getStatusLabel,
    getStatusPillVariant,
    goToList() {
      const backTo = resolveLayoutBackTo(this.$route, '/resource/link/list')
      this.$router.push(backTo)
    },
    goToEdit() {
      const id = this.info.id || this.$route.params.id
      if (!id) {
        this.$message.warning('资源 ID 缺失，无法进入编辑页')
        return
      }
      this.$router.push(
        buildLocationWithBackTo(
          `/resource/link/edit/${id}`,
          this.$route.query && this.$route.query.backTo
        )
      )
    },
    deleteResource() {
      const id = this.info.id || this.$route.params.id
      if (!id) {
        this.$message.warning('资源 ID 缺失，无法删除')
        return
      }
      this.$confirm('此操作将删除该资源, 是否继续?', '提示', { type: 'warning' })
        .then(() => deleteGenericResource({ id }))
        .then((res) => {
          if (res.success) {
            this.$message.success('删除成功')
            this.goToList()
          }
        })
        .catch(() => {})
    },
    toggleOnlineStatus() {
      const id = this.info.id || this.$route.params.id
      if (!id) {
        return
      }
      const nextStatus = this.info.onOrOff === 1 ? 0 : 1
      onOrOff({ id, onOrOff: nextStatus }).then((res) => {
        if (res.success) {
          this.$message.success(nextStatus === 1 ? '已上线' : '已下线')
          this.loadDetail()
        }
      })
    },
    downloadResourceZip() {
      downloadResourceZipFile(this.info).then((result) => {
        if (!result.ok) {
          this.$message.warning(result.message || '下载失败')
          return
        }
        this.$message.success('已开始下载 zip 包')
      })
    },
    copyKey() {
      const key = this.info.key
      if (!key) {
        return
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(key).then(() => {
          this.$message.success('Key 已复制')
        })
        return
      }
      this.$message.warning('当前环境不支持自动复制')
    },
    stopProcessingPoll() {
      if (this.processingTimer) {
        clearInterval(this.processingTimer)
        this.processingTimer = null
      }
    },
    syncProcessingPoll() {
      if ((this.info.onOrOff === 10 || this.info.onOrOff === 12) && !this.processingTimer) {
        this.processingTimer = setInterval(() => {
          this.loadDetail({ silent: true })
        }, 3000)
        return
      }
      if (this.info.onOrOff !== 10 && this.info.onOrOff !== 12) {
        this.stopProcessingPoll()
      }
    },
    loadDetail({ silent } = {}) {
      if (!silent) {
        this.loading = true
        this.loadError = ''
      }
      getGenericResourceById({ id: this.$route.params.id })
        .then((res) => {
          if (!res.success || !res.data) {
            if (!silent) {
              this.loadError = '未获取到资源详情，请返回列表重试'
            }
            return
          }
          const detail = normalizeGenericResourceDetail(res.data)

          if (detail.androidNeglectVersion) {
            detail.androidNeglectVersion = detail.androidNeglectVersion.join(',')
          }
          if (detail.iosNeglectVersion) {
            detail.iosNeglectVersion = detail.iosNeglectVersion.join(',')
          }

          detail.wallpaperAppVersionList = normalizeAppVersionListForView(
            detail.wallpaperAppVersionList
          )

          if (!detail.jsonConfig) {
            detail.jsonConfig = {}
          }
          const extaObj = parseExtaFromConfig(detail.jsonConfig)
          if (extaObj) {
            detail.jsonConfig.exta = extaObj
          }
          if (!detail.countryList) {
            detail.countryList = { isExclude: true, countries: [] }
          }

          this.info = detail
          const covers = resolveCollectionCoverUrls(extaObj)
          this.coverGender = covers.female || !covers.male ? 'female' : 'male'
        })
        .catch(() => {
          if (!silent) {
            this.loadError = '网络异常，请稍后重试'
          }
        })
        .finally(() => {
          if (!silent) {
            this.loading = false
          }
          this.syncProcessingPoll()
        })
    },
  },
}
</script>

<style scoped>
@import './resourceFormPage.css';
@import './linkListTheme.css';
@import './linkModuleSurfaces.css';
@import './linkTypography.css';
@import './linkTableHybrid.css';
@import './linkSemanticPills.css';

.divContainer,
.linkInfoPage__layout {
  height: 100%;
}

.fullContainer {
  width: 100%;
  height: 100%;
}

.linkInfoPage__layout {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: var(--link-canvas);
}

.linkInfoPage__main {
  flex: 1;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 20px;
  overflow: auto;
}

/* JSON 抽屉展开：锁住主区滚动，滚动仅发生在抽屉内 */
.linkInfoPage__main--jsonOpen {
  overflow: hidden;
}

.linkInfoSection {
  margin-bottom: 16px;
}

.linkInfoSection__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.linkInfoSection__title {
  font-size: var(--link-text-lg);
  font-weight: var(--link-font-semibold);
}

.linkInfoSection__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-medium);
  color: var(--link-ink-secondary);
  background: var(--link-surface-muted, #f0ebe3);
}

.linkInfoSection__empty {
  font-size: var(--link-text-md);
  color: var(--link-ink-muted);
}

/* 基本信息：左图 200px + 右文，字段 2 列网格 */
.linkInfoBasic {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.linkInfoBasic__body {
  min-width: 0;
}

.linkInfoBasic__name {
  margin: 0 0 12px;
  font-size: var(--link-text-xl);
  font-weight: var(--link-font-semibold);
  color: var(--link-ink);
  line-height: var(--link-leading-tight);
  word-break: break-word;
}

.linkInfoBasic__keyRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--link-line, #eee9e2);
}

.linkInfoBasic__keyLabel {
  font-size: var(--link-text-sm);
  font-weight: var(--link-font-medium);
  color: var(--link-ink-secondary);
}

.linkInfoBasic__keyValue {
  font-size: var(--link-text-sm);
  color: var(--link-ink-muted);
  background: var(--link-surface-muted, #f0ebe3);
  padding: 2px 8px;
  border-radius: 6px;
}

.linkInfoBasic__copyBtn {
  padding: 0 6px;
  border: none;
  background: none;
  font-size: var(--link-text-xs);
  color: var(--link-accent);
  cursor: pointer;
}

.linkInfoBasic__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 32px;
}

.linkInfoBasic__cell {
  min-width: 0;
}

.linkInfoBasic__labelRow {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  margin-bottom: 6px;
}

.linkInfoBasic__label--inline {
  margin-bottom: 0;
  flex-shrink: 0;
}

.linkInfoBasic__hint {
  margin: 0;
  flex: 1;
  min-width: 12rem;
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-regular);
  color: var(--link-ink-muted);
  line-height: 1.45;
}

.linkInfoBasic__label {
  margin-bottom: 6px;
  font-size: var(--link-text-sm);
  font-weight: var(--link-font-medium);
  color: var(--link-ink-secondary);
}

.linkInfoBasic__tagRow {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.linkInfoBasic__categoryLink {
  text-decoration: none;
}

/* Pill 结构样式；语义色见 linkSemanticPills.css */
.linkInfoPill {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 2px 10px;
  border-radius: var(--link-radius-pill, 999px);
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-medium);
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.linkInfoPill--type {
  font-weight: var(--link-font-semibold);
  border: none;
}

.linkInfoPill--category {
  border: none;
}

.linkInfoPill--dataset,
.linkInfoPill--meta,
.linkInfoPill--tag {
  background: var(--link-surface-muted, #f7f4ef);
  border: 1px solid var(--link-line, #eee9e2);
  color: var(--link-ink-secondary, #57534e);
}

.linkInfoPill--dataset {
  flex: 0 1 auto;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
}

.linkInfoPill--meta,
.linkInfoPill--tag {
  flex: 0 1 auto;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
  text-align: left;
}

.linkInfoPreview__img {
  display: block;
  width: 200px;
  height: 200px;
  border-radius: 12px;
  border: 1px solid var(--link-line, #eee9e2);
  overflow: hidden;
  cursor: zoom-in;
}

.linkInfoPreview__img >>> .el-image__inner,
.linkInfoPreview__img ::v-deep .el-image__inner {
  width: 200px;
  height: 200px;
  object-fit: cover;
}

.linkInfoBasic__preview {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

.linkInfoPreview__genderSwitch {
  display: inline-flex;
  gap: 4px;
  padding: 2px;
  border-radius: 8px;
  background: var(--link-surface-muted, #f0ebe3);
}

.linkInfoPreview__genderBtn {
  appearance: none;
  border: 0;
  margin: 0;
  padding: 4px 10px;
  border-radius: 6px;
  background: transparent;
  font-size: var(--link-text-xs);
  color: var(--link-ink-muted);
  cursor: pointer;
}

.linkInfoPreview__genderBtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.linkInfoPreview__genderBtn--active {
  background: var(--link-surface, #fff);
  color: var(--link-ink, #1f1a14);
  font-weight: var(--link-font-medium);
  box-shadow: 0 1px 2px rgba(31, 26, 20, 0.08);
}

.linkInfoPreview__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 200px;
  height: 200px;
  border-radius: 12px;
  border: 1px dashed var(--link-line-strong, #e5dfd6);
  background: var(--link-surface-muted, #f0ebe3);
  font-size: var(--link-text-sm);
  color: var(--link-ink-muted);
}

.linkInfoPreview__empty i {
  font-size: var(--link-icon-xl);
}

.linkInfoPage .statusPill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px 3px 6px;
  border-radius: 999px;
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-medium);
  line-height: 1;
  white-space: nowrap;
}

.linkInfoPage .statusPill__dot {
  flex-shrink: 0;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.9;
}

.linkInfoPage .statusPill--online {
  background: rgba(167, 243, 208, 0.4);
  color: var(--link-status-success-ink);
}

.linkInfoPage .statusPill--offline {
  background: var(--link-workspace, #f3f0ea);
  color: var(--link-ink-muted);
}

.linkInfoPage .statusPill--pending {
  background: var(--link-status-warning-bg);
  color: var(--link-status-warning-ink);
}

.linkInfoPage .statusPill--failed {
  background: var(--link-status-error-bg);
  color: var(--link-status-error-ink);
}

.linkInfoTagList {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 6px;
  width: 100%;
}

.linkInfoRemark__label {
  margin-bottom: 6px;
  font-size: var(--link-text-sm);
  font-weight: var(--link-font-medium);
  color: var(--link-ink-secondary);
}

.linkInfoRemark__text {
  margin: 0;
  font-size: var(--link-text-md);
  color: var(--link-ink);
  line-height: var(--link-leading-normal);
  white-space: pre-wrap;
  word-break: break-word;
}

.linkInfoPage__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 100%;
  padding: 24px;
}

.linkInfoPage__errorTitle {
  margin: 0;
  font-size: var(--link-text-lg);
  font-weight: var(--link-font-semibold);
}

.linkInfoPage__errorDesc {
  margin: 0;
}

@media (max-width: 720px) {
  .linkInfoBasic {
    grid-template-columns: 1fr;
  }

  .linkInfoBasic__grid {
    grid-template-columns: 1fr;
  }

  .linkInfoPreview__img,
  .linkInfoPreview__empty {
    width: 100%;
    max-width: 240px;
    height: auto;
    aspect-ratio: 1;
  }
}
</style>
